import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Vehicle, shortWon } from '../types/vehicle';
import { Deal, Valuation } from '../types/deal';
import { Company } from '../types/company';
import { Cashflow } from '../types/cashflow';
import { computeMetrics, mult, pct, xirr } from '../lib/metrics';
import { todayLocal } from '../lib/date';
import { RISK_BAR, RISK_GRADES, RISK_STYLE, RiskGrade } from '../lib/risk';
import { riskApi } from '../services/riskApi';

/**
 * 성과 대시보드의 8개 영역 — factsheet 소개서 8쪽 대조 (2026-10-08).
 *
 * 필터 · 핵심 성과 요약 · 리스크 등급 · 투자 배분 · 섹터별 성과 · 딜 프로세스 · Exit 성과 · 포트폴리오 기여도.
 *
 * ⚠️ 「전체」를 두지 않는다. 고유자금과 위탁자금을 합치면 회사 자산이 부풀려 보인다 — 조회 범위는
 *    위탁자금 · 고유자금 · 자금원 하나 중에서 고른다.
 */
type Scope = 'entrusted' | 'own' | number;

const Bar: React.FC<{ part: number; whole: number; cls: string }> = ({ part, whole, cls }) => (
  <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
    <div className={`h-full ${cls}`} style={{ width: `${whole > 0 ? Math.max(2, (part / whole) * 100) : 0}%` }} />
  </div>
);

const Card: React.FC<{ title: string; tour?: string; wide?: boolean; children: React.ReactNode }> = ({ title, tour, wide, children }) => (
  <section data-tour={tour} className={`bg-white border border-slate-200 rounded-lg ${wide ? 'lg:col-span-2' : ''}`}>
    <h3 className="text-sm font-semibold text-slate-900 px-4 py-3 border-b border-slate-100">{title}</h3>
    <div className="px-4 py-3">{children}</div>
  </section>
);

export const PortfolioInsights: React.FC<{
  vehicles: Vehicle[]; deals: Deal[]; companies: Company[]; flows: Cashflow[]; values: Record<number, Valuation>;
}> = ({ vehicles, deals, companies, flows, values }) => {
  const [scope, setScope] = useState<Scope>('entrusted');
  const [period, setPeriod] = useState<'all' | 'ytd' | '1y' | '3y'>('all');
  const [riskBase, setRiskBase] = useState<'cost' | 'value'>('cost');
  // 건별 최신 승인 등급 — 대시보드가 넘기지 않으므로 여기서 읽는다
  const [grades, setGrades] = useState<Record<number, RiskGrade>>({});
  useEffect(() => { riskApi.latestGrades().then(setGrades).catch(() => setGrades({})); }, []);

  const ids = useMemo(() => new Set(vehicles.filter((v) =>
    scope === 'entrusted' || scope === 'own' ? v.kind === scope : v.id === scope).map((v) => v.id)), [vehicles, scope]);

  // 분석 기간은 「그 기간에 투자한 건」으로 거른다 — factsheet 의 기간 프리셋(YTD · 최근 1년 · 최근 3년)
  const now = new Date();
  const cutoff = period === 'all' ? '' : period === 'ytd' ? `${now.getFullYear()}-01-01`
    : todayLocal(new Date(now.getFullYear() - (period === '1y' ? 1 : 3), now.getMonth(), now.getDate()));
  const myDeals = deals.filter((d) => ids.has(d.vehicleId) && d.stage !== '검토' && (d.investedOn ?? '') >= cutoff);
  const dealIds = new Set(myDeals.map((d) => d.id));
  const open = (d: Deal) => d.stage !== '회수' && d.stage !== '상각';
  const cName = (id: number) => companies.find((c) => c.id === id)?.name ?? '—';
  const sector = (id: number) => companies.find((c) => c.id === id)?.sector ?? '미분류';

  const rows = myDeals.map((d) => {
    const fs = flows.filter((f) => f.dealId === d.id);
    const inv = fs.filter((f) => f.kind === 'investment').reduce((s, f) => s + f.amount, 0);
    const ret = fs.filter((f) => f.kind === 'exit' || f.kind === 'dividend').reduce((s, f) => s + f.amount, 0);
    const val = open(d) ? (values[d.id]?.ourValue ?? 0) : 0;
    const grade: RiskGrade | '미평가' = open(d) ? (grades[d.id] ?? '미평가') : '미평가';
    // 평가액 없는 보유 건 — 손익 · 배수를 셀 수 없다(0 으로 세면 원금 전액 손실처럼 보인다, 2026-11-02)
    const unvalued = open(d) && inv > 0 && !values[d.id];
    return { d, fs, inv, ret, val, gain: ret + val - inv, grade, unvalued };
  });
  const anyUnvalued = rows.some((r) => r.unvalued);

  const m = computeMetrics(flows.filter((f) => f.dealId !== null && dealIds.has(f.dealId)), rows.reduce((s, r) => s + r.val, 0));
  const totalInv = rows.reduce((s, r) => s + r.inv, 0);

  // factsheet 처럼 투자원금 기준 · 지분가치(평가액) 기준을 고른다
  const risk = ([...RISK_GRADES, '미평가'] as const).map((g) => ({
    g, amt: rows.filter((r) => open(r.d) && r.grade === g).reduce((s, r) => s + (riskBase === 'cost' ? r.inv : r.val), 0),
    n: rows.filter((r) => open(r.d) && r.grade === g).length,
  }));
  const openInv = risk.reduce((s, r) => s + r.amt, 0);

  const byVehicle = vehicles.filter((v) => ids.has(v.id)).map((v) => ({
    name: v.name, amt: rows.filter((r) => r.d.vehicleId === v.id).reduce((s, r) => s + r.inv, 0),
  })).filter((x) => x.amt > 0);

  const sectors = Array.from(new Set(rows.map((r) => sector(r.d.companyId)))).map((s) => {
    const rs = rows.filter((r) => sector(r.d.companyId) === s);
    const inv = rs.reduce((a, r) => a + r.inv, 0);
    const tot = rs.reduce((a, r) => a + r.ret + r.val, 0);
    return { s, n: rs.length, inv, tot, moic: inv && !rs.some((r) => r.unvalued) ? tot / inv : null };
  }).sort((a, b) => b.inv - a.inv);

  const exits = rows.filter((r) => r.d.stage === '회수').map((r) => {
    const series: Array<[Date, number]> = r.fs
      .filter((f) => f.kind === 'investment' || f.kind === 'exit' || f.kind === 'dividend')
      .map((f) => [new Date(f.occurredOn), f.kind === 'investment' ? -f.amount : f.amount]);
    series.sort((a, b) => a[0].getTime() - b[0].getTime());
    const first = series[0]?.[0]; const last = series[series.length - 1]?.[0];
    const years = first && last ? (last.getTime() - first.getTime()) / 86_400_000 / 365 : 0;
    return { ...r, irr: xirr(series), years };
  });

  const contrib = [...rows].sort((a, b) => Number(a.unvalued) - Number(b.unvalued) || b.gain - a.gain);
  const maxGain = Math.max(1, ...contrib.filter((r) => !r.unvalued).map((r) => Math.abs(r.gain)));

  const chip = (on: boolean) => `px-2.5 py-1 rounded-lg text-xs font-medium border ${on ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`;

  return (
    <div className="space-y-4 mb-6">
      <div data-tour="filter" className="flex flex-wrap items-center gap-x-5 gap-y-2 bg-white border border-slate-200 rounded-lg px-4 py-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-slate-400 mr-1">조회 범위</span>
          <button className={chip(scope === 'entrusted')} onClick={() => setScope('entrusted')}>위탁자금</button>
          <button className={chip(scope === 'own')} onClick={() => setScope('own')}>고유자금</button>
          {vehicles.map((v) => <button key={v.id} className={chip(scope === v.id)} onClick={() => setScope(v.id)}>{v.name}</button>)}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-slate-400 mr-1">투자 기간</span>
          {([['all', '전체 기간'], ['ytd', 'YTD'], ['1y', '최근 1년'], ['3y', '최근 3년']] as const).map(([p, l]) => (
            <button key={p} className={chip(period === p)} onClick={() => setPeriod(p)}>{l}</button>
          ))}
        </div>
      </div>

      <div data-tour="kpi" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {[
          ['총 투자원금', shortWon(m.invested)], ['포트폴리오 현재 가치', shortWon(m.residual)], ['총 회수', shortWon(m.returned)],
          ['MOIC', anyUnvalued ? '—' : mult(m.tvpi)], ['IRR', anyUnvalued ? '—' : pct(m.irr)], ['포트폴리오 수', String(rows.length)],
        ].map(([k, v]) => (
          <div key={k} className="bg-white border border-slate-200 rounded-lg px-3.5 py-2.5">
            <div className="text-xs text-slate-400">{k}</div>
            <div className="mt-0.5 text-lg font-bold text-slate-900 tabular-nums">{v}</div>
          </div>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="리스크 등급별 비중" tour="risk">
          <div className="flex gap-1 mb-2">
            <button className={chip(riskBase === 'cost')} onClick={() => setRiskBase('cost')}>투자원금 기준</button>
            <button className={chip(riskBase === 'value')} onClick={() => setRiskBase('value')}>지분가치 기준</button>
          </div>
          {openInv === 0 ? <p className="text-sm text-slate-500 py-4 text-center">보유 중인 건 없음</p> : (
            <>
              <div className="flex h-3 rounded-full overflow-hidden mb-3">
                {risk.filter((r) => r.amt > 0).map((r) => (
                  <div key={r.g} className={RISK_BAR[r.g]} style={{ width: `${(r.amt / openInv) * 100}%` }} title={`${r.g} ${shortWon(r.amt)}`} />
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {risk.map((r) => (
                  <div key={r.g} className="flex items-center justify-between gap-2 text-xs">
                    <span className={`rounded-full px-2 py-0.5 font-medium ${RISK_STYLE[r.g]}`}>{r.g}</span>
                    <span className="tabular-nums text-slate-600">{r.n}건 · {shortWon(r.amt)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>

        <Card title="투자 배분 · 자금원별">
          {byVehicle.length === 0 ? <p className="text-sm text-slate-500 py-4 text-center">투자 없음</p> : (
            <div className="space-y-2.5">
              {byVehicle.map((x) => (
                <div key={x.name}>
                  <div className="flex justify-between text-xs mb-1"><span className="text-slate-700">{x.name}</span><span className="tabular-nums text-slate-500">{shortWon(x.amt)} · {Math.round((x.amt / totalInv) * 100)}%</span></div>
                  <Bar part={x.amt} whole={totalInv} cls="bg-indigo-500" />
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="투자 연도별 · 건수와 금액">
          {(() => {
            const years = Array.from(new Set(rows.map((r) => (r.d.investedOn ?? '').slice(0, 4)).filter(Boolean))).sort();
            const by = years.map((y) => ({ y, n: rows.filter((r) => r.d.investedOn?.startsWith(y)).length, amt: rows.filter((r) => r.d.investedOn?.startsWith(y)).reduce((s, r) => s + r.inv, 0) }));
            const max = Math.max(1, ...by.map((x) => x.amt));
            return by.length === 0 ? <p className="text-sm text-slate-500 py-4 text-center">투자 없음</p> : (
              <div className="space-y-2">{by.map((x) => (
                <div key={x.y} className="grid grid-cols-[44px_1fr_110px] items-center gap-2 text-xs">
                  <span className="tabular-nums text-slate-600">{x.y}</span>
                  <Bar part={x.amt} whole={max} cls="bg-teal-500" />
                  <span className="text-right tabular-nums text-slate-700">{x.n}건 · {shortWon(x.amt)}</span>
                </div>))}</div>
            );
          })()}
        </Card>

        <Card title="섹터별 투자 성과">
          <table className="w-full text-sm">
            <thead><tr className="text-xs text-slate-400"><th className="text-left font-medium pb-1.5">섹터</th><th className="text-right font-medium">건</th><th className="text-right font-medium">투자</th><th className="text-right font-medium">가치(회수+평가)</th><th className="text-right font-medium">MOIC</th></tr></thead>
            <tbody>
              {sectors.map((x) => (
                <tr key={x.s} className="border-t border-slate-50">
                  <td className="py-1.5">{x.s}</td><td className="text-right tabular-nums">{x.n}</td>
                  <td className="text-right tabular-nums">{shortWon(x.inv)}</td><td className="text-right tabular-nums">{shortWon(x.tot)}</td>
                  <td className={`text-right tabular-nums font-semibold ${x.moic === null ? 'text-slate-400' : x.moic >= 1 ? 'text-emerald-700' : 'text-red-600'}`}>{mult(x.moic)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card title="Exit 성과 · IRR">
          {exits.length === 0 ? <p className="text-sm text-slate-500 py-4 text-center">회수 완료 건 없음</p> : (
            <table className="w-full text-sm">
              <thead><tr className="text-xs text-slate-400"><th className="text-left font-medium pb-1.5">투자처</th><th className="text-right font-medium">투자</th><th className="text-right font-medium">회수</th><th className="text-right font-medium">보유</th><th className="text-right font-medium">MOIC</th><th className="text-right font-medium">IRR</th></tr></thead>
              <tbody>
                {exits.map((x) => (
                  <tr key={x.d.id} className="border-t border-slate-50">
                    <td className="py-1.5"><Link to={`/deals/${x.d.id}`} className="hover:text-indigo-600">{cName(x.d.companyId)}</Link></td>
                    <td className="text-right tabular-nums">{shortWon(x.inv)}</td><td className="text-right tabular-nums">{shortWon(x.ret)}</td>
                    <td className="text-right tabular-nums">{x.years.toFixed(1)}년</td>
                    <td className="text-right tabular-nums font-semibold text-emerald-700">{mult(x.inv ? x.ret / x.inv : null)}</td>
                    <td className="text-right tabular-nums">{pct(x.irr)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="포트폴리오 기여도 · 손익(회수 + 평가 − 투자)" wide>
          <div className="space-y-2">
            {contrib.map((r) => (
              <div key={r.d.id} className="grid grid-cols-[90px_1fr_70px] items-center gap-2 text-xs">
                <Link to={`/deals/${r.d.id}`} className="truncate text-slate-700 hover:text-indigo-600">{cName(r.d.companyId)}</Link>
                {r.unvalued ? (
                  <>
                    <div className="h-2 rounded-full border border-dashed border-slate-200" />
                    <span className="text-right text-slate-400">평가 없음</span>
                  </>
                ) : (
                  <>
                    <div className="relative h-2 bg-slate-100 rounded-full">
                      <div className="absolute top-0 bottom-0 left-1/2 w-px bg-slate-300" />
                      <div className={`absolute top-0 bottom-0 ${r.gain >= 0 ? 'left-1/2 bg-emerald-500 rounded-r-full' : 'right-1/2 bg-red-500 rounded-l-full'}`}
                        style={{ width: `${(Math.abs(r.gain) / maxGain) * 50}%` }} />
                    </div>
                    <span className={`text-right tabular-nums font-medium ${r.gain >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{r.gain === 0 ? '0' : `${r.gain > 0 ? '+' : '−'}${shortWon(Math.abs(r.gain))}`}</span>
                  </>
                )}
              </div>
            ))}
          </div>
        </Card>
      </div>
      <p className="text-xs text-slate-400">리스크 등급(6단계): {RISK_GRADES.join(' · ')} · 미평가 · 승인된 최신 등급 기준</p>
    </div>
  );
};
